import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Shield, Users, Calendar, ArrowRight, MapPin, HeartHandshake } from "lucide-react";

export default function HeroSection({ onBookSpot, onJoinWaitlist, events }) {
  const nextEvent = events && events.length > 0 ? events[0] : null;
  const evTitle = nextEvent?.title || nextEvent?.name || "";
  const evArea = nextEvent?.venueArea || nextEvent?.area || "";
  const evDate = nextEvent?.eventDate || nextEvent?.date || "";
  const evTime = nextEvent?.eventTime || nextEvent?.time || "";
  const evAgeBand = nextEvent?.ageBand || nextEvent?.age_band || "";
  const evSpotsLeftMale = nextEvent?.spotsLeft ?? (nextEvent ? (nextEvent.spots_total || 12) - (nextEvent.spots_taken || 0) : 0);
  const evSpotsLeftFemale = nextEvent?.spotsLeftFemale ?? 0;
  const evMiniDates = nextEvent?.miniDatesPerGuest || 6;
  const evAvgMatches = nextEvent?.avgMatches || 3;

  return (
    <section
      id="hero"
      className="relative min-h-screen flex items-center pt-20 sm:pt-24"
      data-testid="hero-section"
    >
      {/* Background image with overlay */}
      <div className="absolute inset-0 z-0">
        <img
          src="https://images.unsplash.com/photo-1774175927603-129481b459c6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MjJ8MHwxfHNlYXJjaHwyfHxsdXh1cnklMjBjb2NrdGFpbCUyMGJhciUyMGVsZWdhbnR8ZW58MHx8fHwxNzc2MzQ4NzUxfDA&ixlib=rb-4.1.0&q=85"
          alt="Elegant cocktail lounge"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-background/85 dark:bg-background/90" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 sm:px-12 py-16 sm:py-24 w-full">
        <div className="grid lg:grid-cols-5 gap-12 lg:gap-16 items-center">
          {/* Left: Hero Content */}
          <div className="lg:col-span-3">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: "easeOut" }}
            >
              <Badge
                variant="secondary"
                className="rounded-full px-4 py-1.5 text-xs tracking-[0.2em] uppercase font-semibold mb-8 bg-secondary text-secondary-foreground"
                data-testid="hero-badge"
              >
                Pune's premium dating experience
              </Badge>
            </motion.div>

            <motion.h1
              className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-none font-medium text-balance mb-6"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.1, ease: "easeOut" }}
              data-testid="hero-title"
            >
              Real dates, curated nights
              <span className="text-[hsl(var(--accent))]"> in Pune</span>
            </motion.h1>

            <motion.p
              className="text-base sm:text-lg leading-relaxed text-muted-foreground max-w-xl mb-4"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.2, ease: "easeOut" }}
              data-testid="hero-subtitle"
            >
              Premium offline dating mixers at Pune's finest cafes and bars. 
              Hosted speed-dating, icebreaker games, and real 1-on-1 mini-dates — 
              no swiping, no screens.
            </motion.p>

            <motion.div
              className="flex flex-col sm:flex-row gap-4 mb-8"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.3, ease: "easeOut" }}
            >
              <Button
                size="lg"
                onClick={onBookSpot}
                className="rounded-sm px-8 py-6 text-base font-medium tracking-wide transition-all duration-300 hover:-translate-y-0.5 shadow-sm"
                data-testid="hero-book-spot-btn"
              >
                Book a spot — Men &#8377;1,399
                <ArrowRight className="ml-2 w-4 h-4" strokeWidth={1.5} />
              </Button>
              <Button
                size="lg"
                variant="outline"
                onClick={onJoinWaitlist}
                className="rounded-sm px-8 py-6 text-base font-medium tracking-wide transition-all duration-300 hover:-translate-y-0.5"
                data-testid="hero-waitlist-btn"
              >
                Join waitlist — Women free
              </Button>
            </motion.div>

            <motion.div
              className="flex flex-wrap gap-x-6 gap-y-2 text-sm text-muted-foreground"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.7, delay: 0.5 }}
              data-testid="hero-trust-signals"
            >
              <span className="flex items-center gap-1.5">
                <Users className="w-4 h-4 text-[hsl(var(--accent))]" strokeWidth={1.5} />
                Balanced 1:1 ratio
              </span>
              <span className="flex items-center gap-1.5">
                <Shield className="w-4 h-4 text-[hsl(var(--accent))]" strokeWidth={1.5} />
                Verified attendees
              </span>
              <span className="flex items-center gap-1.5">
                <Calendar className="w-4 h-4 text-[hsl(var(--accent))]" strokeWidth={1.5} />
                Contacts shared on mutual match only
              </span>
              <span className="flex items-center gap-1.5">
                <MapPin className="w-4 h-4 text-[hsl(var(--accent))]" strokeWidth={1.5} />
                Public venue events
              </span>
              <span className="flex items-center gap-1.5">
                <HeartHandshake className="w-4 h-4 text-[hsl(var(--accent))]" strokeWidth={1.5} />
                Respect-first policy
              </span>
            </motion.div>
          </div>

          {/* Right: Event Card */}
          <motion.div
            className="lg:col-span-2"
            initial={{ opacity: 0, x: 40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.4, ease: "easeOut" }}
          >
            <div
              className="bg-card border border-border/40 rounded-sm p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)] dark:shadow-none"
              data-testid="hero-event-card"
            >
              <p className="font-mono text-xs tracking-[0.2em] uppercase font-semibold text-[hsl(var(--accent))] mb-4">
                Next event
              </p>
              {nextEvent ? (
                <>
                  <h3 className="font-heading text-xl sm:text-2xl tracking-tight font-medium mb-4">
                    {evTitle}
                  </h3>
                  <div className="space-y-3 mb-6">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Venue area</span>
                      <span className="font-medium">{evArea}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Date & time</span>
                      <span className="font-medium">{evDate} / {evTime}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Age band</span>
                      <span className="font-medium">{evAgeBand}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Mini-dates per guest</span>
                      <span className="font-medium">{evMiniDates}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Avg. matches</span>
                      <span className="font-medium text-[hsl(var(--accent))]">{evAvgMatches}</span>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2 mb-6">
                    {nextEvent.tags && nextEvent.tags.map((tag) => (
                      <Badge
                        key={tag}
                        variant="secondary"
                        className="rounded-full px-3 py-1 text-xs"
                      >
                        {tag}
                      </Badge>
                    ))}
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">
                      {evSpotsLeftMale} Men &amp; {evSpotsLeftFemale} Women spots left
                    </span>
                    <Button
                      size="sm"
                      onClick={() => onBookSpot(nextEvent)}
                      className="rounded-sm px-6 font-medium tracking-wide transition-all duration-300 hover:-translate-y-0.5"
                      data-testid="hero-event-reserve-btn"
                    >
                      Reserve
                    </Button>
                  </div>
                </>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <p>Events loading...</p>
                </div>
              )}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
